import React, { useEffect, useRef, ReactNode } from 'react';
import { Icon } from '@atlassian/learning-components';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';

export interface DropdownLinkProps {
  title: string;
  isOpen: boolean;
  onToggle: (isOpen: boolean) => void;
  children: ReactNode;
  className?: string;
  dropdownPosition?: {
    top?: number;
    left?: number;
    right?: number;
  };
  preventScroll?: boolean;
  openOnHover?: boolean;
}

export const DropdownLink = ({
  title,
  isOpen,
  onToggle,
  children,
  className,
  dropdownPosition = { top: 40, left: 0 },
  preventScroll = true,
  openOnHover = false,
}: DropdownLinkProps): React.ReactElement => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleMenu = (): void => {
    onToggle(!isOpen);
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleMenu();
    }
  };

  const handleMouseEnter = (): void => {
    if (openOnHover) {
      onToggle(true);
    }
  };

  const handleMouseLeave = (): void => {
    if (openOnHover) {
      onToggle(false);
    }
  };

  useEffect(() => {
    if (!preventScroll) {return;}

    const dropdown = dropdownRef.current;

    if (!dropdown) {
      return;
    }

    const preventScrollHandler = (e: WheelEvent | TouchEvent): void => {
      e.preventDefault();
    };

    dropdown.addEventListener('wheel', preventScrollHandler);
    dropdown.addEventListener('touchmove', preventScrollHandler);

    return () => {
      dropdown.removeEventListener('wheel', preventScrollHandler);
      dropdown.removeEventListener('touchmove', preventScrollHandler);
    };
  }, [preventScroll]);

  return (
    <div
      css={css({ position: 'relative' })}
      className={className}
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span
        tabIndex={0}
        css={[dropdownTitleStyles, isOpen && css({ color: token('color.link') })]}
        onClick={openOnHover ? undefined : toggleMenu}
        onKeyDown={handleKeyDown}
      >
        {title}
        <span css={[iconWrapperStyles, isOpen && iconWrapperRotateStyles]}>
          <Icon type="chevron-down" alt={isOpen ? 'Collapse item' : 'Expand item'} />
        </span>
      </span>
      <div
        aria-hidden={!isOpen}
        css={[
          dropdownStyles,
          isOpen && dropdownOpenStyles,
          css({
            ...(dropdownPosition.top !== undefined && { top: dropdownPosition.top }),
            ...(dropdownPosition.left !== undefined && { left: dropdownPosition.left }),
            ...(dropdownPosition.right !== undefined && { right: dropdownPosition.right }),
          }),
        ]}
      >
        {children}
      </div>
    </div>
  );
};

const dropdownTitleStyles = css({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
});

const iconWrapperStyles = css({
  transition: 'transform 0.2s cubic-bezier(0.22, 1, 0.36, 1)',
});

const iconWrapperRotateStyles = css({
  transform: 'rotate(180deg)',
});

const dropdownStyles = css({
  display: 'flex',
  flexDirection: 'column',
  position: 'absolute',
  boxShadow: token('elevation.shadow.overlay'),
  borderRadius: '15px',
  overflow: 'hidden',
  opacity: 0,
  pointerEvents: 'none',
  transform: 'translateY(16px)',
  transition: 'opacity 0.2s cubic-bezier(0.22, 1, 0.36, 1), transform 0.2s cubic-bezier(0.22, 1, 0.36, 1)',
  cursor: 'default',
});

const dropdownOpenStyles = css({
  opacity: 1,
  pointerEvents: 'auto',
  transform: 'translateY(0)',
});
